import {
  IAutoCouponSelectors,
  IAutoCouponSKUSelectors,
  IAutoCouponTimings,
  TTemplateView,
} from "../auto-coupons";
import { FailureReasonType, ICouponResult, ITaskWithMerchant } from "../task";
import { IRecognisePageResponse, PageType } from "./page-recognise";

export const TEMPLATE_ERRORS = {
  COULD_NOT_SUBMIT_CODE: "[SYSTEM] could not submit code",
  COULD_NOT_ENTER_CODE: "[SYSTEM] could not enter code",
  ERROR_APPLYING_CODE: "[SYSTEM] error applying coupon",
  /**
   * The merchant's server took the code and declined it without saying so: the
   * form submitted, no error appeared, the total did not move.
   *
   * NOT prefixed [SYSTEM], for the same reason as COUPON_NOT_REFLECTED_ON_ORDER
   * below — the automation worked. `coupon-application.ts` already sets
   * `systemError: false` for this case (`isSilentRejection`), so the prefix was
   * claiming something the code deliberately contradicted, and put 328 results a
   * day into the dashboard's System Errors column's neighbour while reading as
   * a system message to anyone scanning the logs.
   */
  TOTAL_PRICE_DID_NOT_CHANGE: "coupon did not change the total price",
  COUPON_APPLICATION_FAILED: "[SYSTEM] coupon application encountered an error",
  /**
   * The merchant acknowledged the code in text (a toast, an inline message) but
   * nothing on the order actually moved — no discount row, no reduced total, no
   * applied-code chip. NOT prefixed [SYSTEM]: the automation worked, the order
   * simply never reflected the code, which is a real business outcome and must
   * not be reported as a successful application.
   */
  COUPON_NOT_REFLECTED_ON_ORDER:
    "coupon acknowledged in text but the order total never reflected it",
};

export enum AutomationState {
  INITIALIZATION = "initialization",
  NEW_MERCHANT = "newMerchant",
  PAGE_RECOGNITION = "pageRecognition",
  PRODUCT_SELECTION = "productSelection",
  PRODUCT_SEARCH = "productSearch",
  ADD_TO_CART = "addToCart",
  CART_CHECK = "cartCheck",
  CART_NAVIGATION = "cartNavigation",
  COUPON_APPLICATION = "couponApplication",
  COUPON_VERIFICATION = "couponVerification",
  RESULT_RECORDING = "resultRecording",
  NEXT_COUPON = "nextCoupon",
  HUMAN_INTERVENTION = "humanIntervention",
  RESUME_FROM_HUMAN = "resumeFromHuman",
  POPUPS_HANDLER = "popupsHandler",
  WOO_COMMERCE = "wooCommerce",
  BIG_COMMERCE = "bigCommerce",
  SHOPIFY = "shopify",
  MAGENTO = "magento",
  SALESFORCE = "salesforce",
  SESSION_RESTORE = "sessionRestore",
  AUTO_COUPONS_ENGINE = "autoCouponsEngine",
  TASK_TIMEOUT = "taskTimeout",
  TASK_COMPLETE = "taskComplete",
}

export enum ActionType {
  // Search and navigation
  SEARCH = "SEARCH",
  SEARCH_TOGGLE = "SEARCH_TOGGLE",
  SEARCH_SUBMIT = "SEARCH_SUBMIT",
  CATEGORY_NAVIGATION = "CATEGORY_NAVIGATION",

  // Product selection and configuration
  PRODUCT_SELECT = "PRODUCT_SELECT",
  PRODUCT_GRID_SELECT = "PRODUCT_GRID_SELECT",
  PRODUCT_LINK_SELECT = "PRODUCT_LINK_SELECT",
  PRODUCT_IMAGE_SELECT = "PRODUCT_IMAGE_SELECT",

  // Generic SKU and variant selection (merchant-specific)
  SKU_VARIANT_SELECT = "SKU_VARIANT_SELECT",

  // Cart operations
  ADD_TO_CART = "ADD_TO_CART",
  CART_ACCESS = "CART_ACCESS",
  CART_NAVIGATION = "CART_NAVIGATION",
  CART_QUANTITY_CHECK = "CART_QUANTITY_CHECK",
  CART_ITEM_VERIFICATION = "CART_ITEM_VERIFICATION",
  CONTINUE_SHOPPING = "CONTINUE_SHOPPING",

  // Checkout flow
  CHECKOUT_BUTTON = "CHECKOUT_BUTTON",
  GUEST_CHECKOUT = "GUEST_CHECKOUT",
  CHECKOUT_NEXT_STEP = "CHECKOUT_NEXT_STEP",
  CHECKOUT_PREVIOUS_STEP = "CHECKOUT_PREVIOUS_STEP",

  // Shipping / guest-checkout form-fill + continue-to-payment
  SHIPPING_FIELD_NAME = "SHIPPING_FIELD_NAME",
  SHIPPING_FIELD_ADDRESS = "SHIPPING_FIELD_ADDRESS",
  SHIPPING_FIELD_EMAIL = "SHIPPING_FIELD_EMAIL",
  CHECKOUT_CONTINUE = "CHECKOUT_CONTINUE",

  // Coupon operations
  COUPON_FIELD = "COUPON_FIELD",
  COUPON_APPLY = "COUPON_APPLY",
  COUPON_REMOVE = "COUPON_REMOVE",
  TOGGLE_COUPON_FORM = "TOGGLE_COUPON_FORM",
  TOTAL_PRICE = "TOTAL_PRICE",
  SUCCESS_MESSAGE = "SUCCESS_MESSAGE",
  FAILURE_MESSAGE = "FAILURE_MESSAGE",

  // Modal and popup handling
  CLOSE_MODAL = "CLOSE_MODAL",
  ACCEPT_COOKIES = "ACCEPT_COOKIES",
  DISMISS_NEWSLETTER = "DISMISS_NEWSLETTER",
  CLOSE_POPUP = "CLOSE_POPUP",

  // Cart verification actions
  VERIFY_CART_MINIMUM = "VERIFY_CART_MINIMUM",
  CHECK_CART_EMPTY = "CHECK_CART_EMPTY",

  // General navigation
  BACK_BUTTON = "BACK_BUTTON",
  REFRESH_PAGE = "REFRESH_PAGE",
}

export enum SelectorSource {
  MANUAL = "manual",
  AI_DISCOVERY = "ai_discovery",
  PATTERN_LEARNING = "pattern_learning",
  TECHNOLOGY_DEFAULT = "technology_default",
  // Written back by the verification pass when a broken/absent leg was
  // re-found and confirmed by the judge (see JourneyTestService.tryHeal).
  SELF_HEAL = "self_heal",
  // The live auto-coupon template this run replayed. Distinct from AI_DISCOVERY
  // because nothing was discovered — the walk verified a selector it was given.
  TEMPLATE = "template",
}

export interface SkuOption {
  variantType: string; // e.g., "size", "color", "material"
  variantSelector: string; // CSS selector for the variant
  variantValue?: string; // e.g., "M", "Red", "Cotton"
  confidence?: number; // AI confidence in this specific variant selector
}

export interface ProductVariants {
  productUrl: string;
  options: SkuOption[];
  successCount: number;
  failureCount: number;
}

export interface IProductVariantResponse {
  reason: string;
  confidence: number;
  options: SkuOption[];
}

export interface ProductPage {
  url: string;
  skipSkuCheck: boolean;
  validationSuccessCount: number;
  validationFailureCount: number;
}

export interface IntelligentSelectorSimple {
  selector: string;
  xpathSelector?: string;
  confidence: number;
  reasoning?: string;
  metadata?: Record<string, any>;
}

export interface IntelligentSelector extends IntelligentSelectorSimple {
  successCount: number;
  failureCount: number;
  lastWorked: Date;
  pageTypes?: PageType[];
  discoveredBy?: SelectorSource;
}

export interface UrlPattern {
  regex: string;
  confidence: number;
  examples: string[]; // URLs that match this pattern
  successCount: number;
  failureCount: number;
  lastValidated: Date;
  autoGenerated: boolean;
}

export interface AIAnalysisCache {
  pageRecognitions: Array<{
    urlHash: string;
    pageType: PageType;
    discoveredSelectors: {
      [key in ActionType]?: string[];
    };
    confidence: number;
    timestamp: Date;
    hitCount: number;
  }>;

  selectorDiscoveries: Array<{
    pageContentHash: string;
    requestedAction: ActionType;
    discoveredSelector: string;
    confidence: number;
    timestamp: Date;
    validated: boolean;
  }>;
}

export interface PerformanceMetrics {
  successRates: {
    pageRecognition: number;
    selectorDiscovery: number;
    journeyCompletion: number;
  };
  aiUsage: {
    totalRequests: number;
    cacheHitRate: number;
    averageConfidence: number;
  };
  lastUpdated: Date;
}

export enum checkoutFlowType {
  SINGLE_PAGE = "single_page",
  MULTI_STEP = "multi_step",
  GUEST_AVAILABLE = "guest_available",
  LOGIN_REQUIRED = "login_required",
}

export interface JourneyIntelligence {
  cartRequirements: {
    minimumValue?: number;
    minimumItemCount?: number;
    estimatedTimeToCart: number;
  };
  knownObstacles: Array<{
    obstacle: string;
    pageType: PageType[];
    solution: {
      selector: string;
      action: "click" | "dismiss" | "wait";
      successCount: number;
    };
  }>;
  checkoutFlow: {
    type: checkoutFlowType;
    guestCheckoutAvailable: boolean;
    confidence: number;
  };
}

export type SiteSuitabilityCategory =
  | "TRAVEL_BOOKING"
  | "PROPERTY_BUYING"
  | "INSURANCE"
  | "FERRY_BOOKING"
  | "PARKED_DOMAIN"
  | "DOMAIN_HOSTING"
  | "MEAL_PLANNING"
  | "THEME_PARK_BOOKING"
  | "HOLIDAY_PARK_BOOKING"
  | "LUXURY_VACATION_DEALS"
  | "GAMBLING"
  | "THERAPY_SERVICES"
  | "TRAINING_COURSES";

export interface SiteSuitabilityResult {
  notSuitable: boolean;
  category: SiteSuitabilityCategory | null;
  suitabilityScore: number | null;
  method: "embedding" | "merchant_type";
  synopsisPreview?: string;
  threshold?: number;
  url?: string;
}

export interface ICouponWithMeta {
  code: string;
  publishedStatus: number; // IKindredCouponStatus
  source: "api" | "db";
  brandId?: string;
}

export interface CouponCodeSession {
  startedAt: Date;
  finishedAt?: Date;
  couponsToTest: ICouponWithMeta[];
  currentIndex: number;

  originalTotal: number;
  currency: string;
  currentSavings: number;
  bestCoupon: string | null;

  appliedCoupons: ICouponResult[];

  couponFormReady: boolean;
  couponFormHidden?: boolean;
  /**
   * The previous code's discount could not be removed, so the basket can no
   * longer be used to judge anything.
   *
   * Once set, the remaining codes are abandoned rather than tested against a
   * basket we know is dirty. Each one would otherwise return the same system
   * error: over three days, 87 such baskets produced 342 identical results,
   * mean 3.9 and up to 9 each.
   */
  basketUnclearable?: boolean;
  selectors: {
    couponField?: string;
    submitButton?: string;
    toggleButton?: string;
    totalPrice?: string;
    successMessage?: string;
    errorMessage?: string;
    removeButton?: string;
  };
}

/**
 * Replay metadata for a session run (runType "session"): where the restored
 * browser session came from and what was injected. Written by the
 * SESSION_RESTORE state and persisted onto results for provenance.
 */
export interface ISessionReplayContext {
  /** correlationId of the extension run whose captured session was replayed. */
  sourceRunId: string;
  checkoutUrl: string;
  sessionCapturedAt: string | null;
  sessionAgeMs: number | null;
  injectedCookies: number;
  localStorageItems: number;
  sessionStorageItems: number;
  userAgent: string | null;
  mobileEmulated: boolean;
}

/**
 * The live SQL template halves under test in a session run. `strict: true`
 * means AI selector discovery is disabled — a failing selector is a template
 * failure, not something to repair.
 */
export interface ISessionTemplateTestContext {
  strict: boolean;
  autoTemplateId: string | null;
  autoVersion: number | null;
  /**
   * Where each template half was resolved from. Mirrors the extension's
   * background resolution: Mongo working template first, live SQL fallback.
   */
  autoSource: "mongo" | "sql" | null;
  skuSource: "mongo" | "sql" | null;
  /** SKU template row id (partners DB); filled when the SKU half is loaded. */
  skuTemplateId: string | null;
  /** SKU versions are string labels (e.g. "V1"). */
  skuVersion: string | null;
  templateView: TTemplateView | null;
  checkoutPageUrlRegex: string | null;
  selectors: IAutoCouponSelectors | null;
  timings: IAutoCouponTimings | null;
  skuSelectors: IAutoCouponSKUSelectors | null;
}

/**
 * Outcome data the AUTO_COUPONS_ENGINE state hands to TASK_COMPLETE so the
 * session-run record (one doc per session run) can be written in one place,
 * for every path — including runs that failed before the engine started.
 */
export interface ISessionRunOutcome {
  correlationId: string;
  evaluationId: string | null;
  testResultId: string | null;
  codes: any[];
  capturedItems: any[];
  aiCalled: boolean;
  currency: string | null;
  originalTotal: number | null;
  startedAt: string | null;
  finishedAt: string | null;

  // ── Capture-run only (runType "capture") ──────────────────────────────
  // Populated by AUTO_COUPONS_ENGINE's handleCouponEvaluation when (and only
  // when) it saved a capture run's built template halves back to Mongo
  // staging. Left undefined for session/live/audit runs — consumed by
  // TemplateCaptureRunsService.recordFromContext to decide the run's terminal
  // status (completed vs. failed_other) and the per-half
  // Merchant.autoTemplateCreationStatus/skuTemplateCreationStatus update.
  /** True once the engine attempted an `upsertTemplate` (auto half) save-back. */
  captureAutoAttempted?: boolean;
  /** True iff that `upsertTemplate` call succeeded. */
  captureAutoSaved?: boolean;
  /** The AutoCouponTemplate _id the auto half was saved to, when saved. */
  captureAutoTemplateId?: string | null;
  /** True once the engine attempted an `upsertSKUSelectors` (SKU half) save-back
   *  — false/undefined when the run produced no `skuTemplate` to save at all. */
  captureSkuAttempted?: boolean;
  /** True iff that `upsertSKUSelectors` call succeeded. */
  captureSkuSaved?: boolean;
  /** The SKU template _id the SKU half was saved to, when saved. */
  captureSkuTemplateId?: string | null;
  /** e.g. the caught SKU_INCOMPLETE error's message, when attempted but not saved. */
  captureSkuFailureReason?: string | null;
}

export interface IAdditionalSettings {
  hasSearchToggle?: boolean;
  knownCartPage?: string;
  cartPageIsCheckout?: boolean;
  cartPageHasCouponForm?: boolean;
  cookieDismissalRequired?: boolean;
  newsletterDismissalRequired?: boolean;
  regionalDismissalRequired?: boolean;
  continueOnCouponFailure?: boolean;
  /** Opt this merchant into the daily merchant-map journey test. */
  journeyTestEnabled?: boolean;
  /** When the daily sweep last claimed this merchant (queue ordering). */
  journeyTestLastRunAt?: Date | null;
  /** Device the merchant-map capture emulates. Defaults to mobile in code;
   *  set "desktop" to build/test this merchant's map on desktop instead. */
  mapDevice?: "mobile" | "desktop";
  /** Force this merchant to own its domain's template capture, overriding the
   *  ccTLD-then-preference rule. */
  forcePrimaryGeo?: boolean;
  /** A person chose to run this merchant even though the suitability gate
   *  classes it as a site we don't test. */
  ignoreSiteSuitability?: boolean;
  /**
   * A person asked for a capture of this merchant from the UI.
   *
   * One-shot: the scheduler claims it on the next poll and clears it as the
   * task is created. It deliberately overrides cadence, template ownership and
   * the schedule itself, because it represents someone explicitly asking for
   * THIS merchant now — none of which is true of the daily sweep.
   */
  captureRequestedAt?: Date | null;
  /** The QA list whose "Capture all" created this merchant, if any. */
  createdFromQaListId?: string | null;
}

export interface StateMachineContext {
  currentState: AutomationState;
  /** Deepest state this run reached, for grading how far it got. */
  deepestState?: string;
  previousState: AutomationState;
  browserInstance?: any; // Puppeteer browser instance
  pageInstance?: any; // Puppeteer page instance
  contextInstance?: any; // Puppeteer browser context instance
  taskData: ITaskWithMerchant;
  pageData: {
    url: string;
    title?: string;
    description?: string;
    content?: string;
    synopsis?: string;
    type?: PageType;
  };
  cartData: {
    hasItems: boolean;
    itemCount?: number;
    totalPrice: string;
    itemNames?: string[];
  };
  journeyProgress: {
    currentPhase:
      | "landing"
      | "product_search"
      | "cart_building"
      | "checkout_prep"
      | "checkout";
    stepsCompleted: AutomationState[];
    failedAttempts: Array<{
      state: AutomationState;
      reason: string;
      timestamp: Date;
    }>;
    startTime: Date;
    phaseStartTime: Date;
  };

  checkoutData?: {
    checkoutConfirmed: boolean;
    couponFormReady: boolean;
  };

  discoveredSelectors?: {
    [key in ActionType]?: IntelligentSelector[];
  };

  productVariants?: ProductVariants[];

  /** URL of the last page recognised as each PageType during the walk.
   *  Populated by page-recognition; read by MerchantMapWriter to record
   *  productPage/cart/checkout urlPattern + checkoutUrlRegex. */
  pageUrlsByType?: { [pageType: string]: string };

  /** TemplateCaptureRun correlationId for a capture run, resolved once at
   *  initialisation so every AI call can be charged against its budget. */
  captureCorrelationId?: string | null;

  /** Actions this run has already paid an AI discovery for, so a retry loop
   *  cannot buy the same answer twice. */
  aiDiscoveredActions?: string[];

  /** Cart line-item (SKU) selectors captured off the cart/checkout page BEFORE
   *  the coupon step (see CheckoutStateHandler). Read by MerchantMapWriter into
   *  the cart.item* map fields so they stop showing "not captured". */
  cartItemSelectors?: {
    itemName?: string | null;
    itemPrice?: string | null;
    itemQuantity?: string | null;
    itemCategory?: string | null;
    itemOriginalPrice?: string | null;
    itemProductId?: string | null;
    itemProductUrl?: string | null;
    deliveryAmount?: string | null;
    basketAmount?: string | null;
  };

  /** Stable selectors for the guest-checkout shipping fields, captured while the
   *  fake per-region address is filled in (CheckoutStateHandler). Read by
   *  MerchantMapWriter into checkout.shippingFields.*. */
  shippingFieldSelectors?: {
    name?: string | null;
    address?: string | null;
    email?: string | null;
    continueButton?: string | null;
  };

  /** The checkout is gated behind a login/account wall, so the shipping legs are
   *  not applicable for this merchant rather than a capture failure. */
  checkoutRequiresAuth?: boolean;
  /**
   * Set when a stored session could not be used and the run fell back to the
   * agentic walk. The run still succeeds, so this is the only record that this
   * merchant's session needs replacing — the fallback hides the symptom, not
   * the signal. Consumed by session health / failure-triggered recapture.
   */
  sessionRestoreFailure?: { reason: string; at: string } | null;

  /** Evidence sampled at the moment a selector is found on its OWN page (e.g.
   *  add-to-cart on the product page), keyed by the journey-test node path. The
   *  journey test uses it to validate a leg that the walk ended away from, so it
   *  shows a real match count + value instead of "captured (not on this page)". */
  /** Product URLs this run found unpurchasable (sold out), and how many times it
   *  has walked away from one. Bounded so a merchant whose whole catalogue is
   *  out of stock fails with a reason instead of circling home → search → PDP
   *  forever. */
  unbuyableProductUrls?: string[];

  capturedEvidence?: {
    [node: string]: {
      matched: number;
      text: string | null;
      tag: string | null;
      /** The selector this evidence was read through, and the one it replaced
       *  when the walk re-pointed the leg (e.g. a total selector left on the
       *  pre-coupon figure). Together they say which stored node this reading
       *  actually describes. */
      selector?: string | null;
      previousSelector?: string | null;
    };
  };

  navigationHistory: Array<{
    url: string;
    pageType: PageType;
    timestamp: Date;
    action: ActionType;
    success: boolean;
  }>;

  aiDecisions: {
    lastPageAnalysis?: IRecognisePageResponse;
    lastActionDecision?: {
      recommendedAction: AutomationState;
      reasoning: string;
      confidence: number;
      timestamp: Date;
    };
  };

  humanInterventionData?: {
    interventionId: string;
    reason: string;
    stateToResumeFrom: AutomationState;
    awaitingUserInput: boolean;
    timestamp: Date;
    context: Record<string, any>;
  };
  metrics: {
    startTime: string;
    totalAiRequests: number;
    cacheHitRate: number;
    averageStateExecutionTime: number;
    lastStateTransition: string;
    // Stuck state detection
    stuckState?: {
      [stateKey: string]: {
        url: string;
        fingerprint: string;
        count: number;
        recoveryCount: number;
        lastUpdated: string;
        progressKey?: string;
      };
    };
    // Vision fallback metrics
    visionMetrics?: {
      totalCalls: number;
      successfulCalls: number;
      totalCost: number;
    };
  };
  urlPatterns?: {
    [key in PageType]?: Array<UrlPattern>;
  };

  couponSession?: CouponCodeSession;
  sessionReplay?: ISessionReplayContext;
  templateTest?: ISessionTemplateTestContext;
  sessionRunOutcome?: ISessionRunOutcome;
  cachedProductPages?: ProductPage[];
  skipSkuCheck?: boolean;
  hasDismissedCookies?: boolean;
  hasDismissedNewsletter?: boolean;
  hasDismissedRegional?: boolean;
  iframeNavigation?: {
    attempted?: boolean;
    navigated?: boolean;
    lastSrc?: string;
    lastPageUrl?: string;
  };

  // TODO implement a retry or fail mechanism, so we're not trying to make too many calls
  numberOfLlmCallFailures: number;

  isTaskCancelled: boolean;

  ranSuccessfully?: boolean;
  errorMessage?: string;
  errorType?: FailureReasonType;
  requiredHumanIntervention?: boolean;
  /** The coupon was applied on the cart page — this merchant has no separate
   *  checkout step, so `cart.checkoutSelector` is not a required leg. */
  cartIsCheckout?: boolean;
  /** A page has already been judged UNKNOWN once; the walk gets one product
   *  search to recover before the run is given up. */
  unknownPageRetried?: boolean;
  /** The path initialisation chose. `taskData.executionMode` is only the forced one. */
  resolvedExecutionMode?: "replay" | "api" | "walk";
  /** Why the walk last bounced back to page recognition. The loop guard reports
   *  this instead of the bare fact that it looped. */
  lastBounce?: {
    reason: FailureReasonType;
    detail: string;
    url?: string | null;
  };
  notSuitable?: boolean;
  siteSuitability?: SiteSuitabilityResult;

  activityCount: {
    [key in AutomationState]?: number;
  };
}

export interface StateHandler {
  enter: (context: StateMachineContext) => Promise<void>;
  execute: (context: StateMachineContext) => Promise<AutomationState>;
  exit: (
    context: StateMachineContext,
    nextState: AutomationState
  ) => Promise<void>;
}
